/**
 * ETL lanes (corsie) — a small CRUD over the predefined serialization domains
 * (ADR-0038 concurrency). A lane is just a named label; a pipeline opts into one
 * (from the editor). Runs of pipelines sharing a lane never overlap.
 *
 * Deleting a lane is non-blocking: its pipelines are unassigned (ON DELETE SET NULL).
 */
import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Split, Trash2 } from "lucide-react";

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EtlNavTabs } from "@/components/etl/EtlNavTabs";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { etlApi, type EtlLane } from "@/lib/api-etl";
import { usePermissions } from "@/lib/permissions";

function LaneDialog({ lane, onClose }: { lane: EtlLane | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = React.useState(lane?.name ?? "");
  const [description, setDescription] = React.useState(lane?.description ?? "");

  const saveMut = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), description: description.trim() || undefined };
      return lane ? etlApi.lanes.update(lane.id, body) : etlApi.lanes.create(body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["etl", "lanes"] });
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{lane ? `Corsia · ${lane.name}` : "Nuova corsia"}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Nome</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="es. magazzino-notturno"
              autoFocus
              maxLength={64}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Descrizione</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Facoltativa — a cosa serve questa corsia"
            />
          </div>
          <p className="text-[11px] text-muted-foreground">
            Le pipeline assegnate a questa corsia non vengono mai eseguite contemporaneamente:
            i run si mettono in fila. L'assegnazione si fa dall'editor della pipeline.
          </p>
          {saveMut.isError && (
            <p className="text-[11px] text-danger">{(saveMut.error as Error).message}</p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => saveMut.mutate()} disabled={!name.trim() || saveMut.isPending}>
            {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EtlLanesPage() {
  const { can } = usePermissions();
  const qc = useQueryClient();
  const [editing, setEditing] = React.useState<EtlLane | null | "new">(null);

  const q = useQuery({ queryKey: ["etl", "lanes"], queryFn: () => etlApi.lanes.list() });
  const delMut = useMutation({
    mutationFn: (id: string) => etlApi.lanes.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["etl", "lanes"] }),
  });

  const rows = q.data ?? [];
  const canWrite = can("etl", "write");
  const canDelete = can("etl", "delete");

  return (
    <PageContainer>
      <PageHeader
        title="Corsie ETL"
        description="Domini di serializzazione: pipeline nella stessa corsia non girano mai insieme."
        actions={
          canWrite && (
            <Button onClick={() => setEditing("new")}>
              <Plus className="h-4 w-4" /> Nuova corsia
            </Button>
          )
        }
      />

      <PageContent>
        <EtlNavTabs active="lanes" />
        {q.isLoading ? (
          <div className="flex items-center gap-2 p-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Split}
            title="Nessuna corsia"
            description="Crea una corsia per serializzare le pipeline che toccano lo stesso dominio dati."
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>Nome</Th>
                  <Th>Descrizione</Th>
                  <Th className="w-28">Pipeline</Th>
                  {(canWrite || canDelete) && <Th className="w-16 text-right">Azioni</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((lane) => (
                  <Tr
                    key={lane.id}
                    className={canWrite ? "cursor-pointer" : ""}
                    onClick={() => canWrite && setEditing(lane)}
                  >
                    <Td className="font-medium text-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <Split className="h-3.5 w-3.5 text-muted-foreground" /> {lane.name}
                      </span>
                    </Td>
                    <Td className="max-w-md truncate text-xs text-muted-foreground">
                      {lane.description || "—"}
                    </Td>
                    <Td className="text-xs text-muted-foreground">{lane.pipeline_count}</Td>
                    {(canWrite || canDelete) && (
                      <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                        {canDelete && (
                          <button
                            type="button"
                            onClick={() => {
                              const msg = lane.pipeline_count
                                ? `Eliminare la corsia "${lane.name}"? ${lane.pipeline_count} pipeline verranno scollegate.`
                                : `Eliminare la corsia "${lane.name}"?`;
                              if (window.confirm(msg)) delMut.mutate(lane.id);
                            }}
                            className="rounded p-1 text-muted-foreground hover:bg-elevated hover:text-danger"
                            aria-label="Elimina"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </Td>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </PageContent>

      {editing !== null && (
        <LaneDialog lane={editing === "new" ? null : editing} onClose={() => setEditing(null)} />
      )}
    </PageContainer>
  );
}
